import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

// Formulários de requerimento do DETRAN/RS trazidos da Nexus CRVA (RequerimentoDetranForm), sem mudar o
// layout nem a impressão: requerimento de serviço com declaração e requerimento de primeiro emplacamento.
// Os campos são preenchidos/marcados direto na folha. Abertos pela RequerimentoDetranPage.

// ─── Dados estáticos ──────────────────────────────────────────────────────────

const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"
];

const SERVICOS_ESQUERDA = [
  "2ª via do CRV",
  "Impressão do CRLVe",
  "Emissão de Certidão",
  "Cópia de documentos",
  "Licença Especial de Trânsito",
  "Alteração de Informações do Proprietário",
  "Alteração de Informações do Veículo",
  "Inclusão de Restrição Financeira",
  "Alteração de Restrição Financeira",
  "Liberação de Restrição Financeira",
  "Inclusão de Averbação de Execução",
  "Liberação de Averbação de Execução",
  "Restrição por Transferência",
  "Inclusão Placa de Experiência ou de Fabricante",
  "Renovação de Placa de Experiência ou de Fabricante",
  "Baixa de Placa de Experiência ou de Fabricante",
  "Baixa para outra UF",
  "Baixa simples de Veículo",
  "Baixa para militarização",
  "Baixa para outro país",
  "Cancelamento de Processo",
  "Comunicação de Venda",
];

const SERVICOS_DIREITA = [
  "Correção das Observações do CRLV-e",
  "Correção de Chassi",
  "Correção de Informações do Proprietário",
  "Correção de Informações do Veículo",
  "Correção de Município",
  "Correção de Restrições",
  "Mudança p/ Placa Única / Troca de Placa Mercosul",
  "Primeiro Emplacamento",
  "Reserva de Placa",
  "Autorização para Fabricação de Placas",
  "Colocação de lacre em placa",
  "Autorização Para Alteração de Características",
  "Autorização para Regravação de Nº de Chassi",
  "Autorização para Regravação de Nº de Motor",
  "Autorização para Transporte Escolar",
  "Solicitação de Vistoria",
  "Transferência de propriedade de Veículo de Outra UF",
  "Transferência de Propriedade de Veículo do RS",
  "Troca de Município de Veículo de Outra UF",
  "Troca de Município de Veículo do RS",
];

const SERVICOS_COM_SUBOPCAO = [
  "Autorização para Fabricação de Placas",
  "Colocação de lacre em placa",
];

// ─── Print Global Styles ──────────────────────────────────────────────────────

const PrintStyles = createGlobalStyle`
  @media print {
    @page {
      size: A4 portrait;
      margin: 1.2cm 0.8cm;
    }
    body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .no-print {
      display: none !important;
    }
    input::placeholder {
      color: transparent !important;
    }
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`;

// ─── Formatadores ─────────────────────────────────────────────────────────────

const fmt = {
  cpf:  (v) => v ? v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '',
  cnpj: (v) => v ? v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : '',
  tel:  (v) => {
    if (!v) return '';
    const d = v.replace(/\D/g, '');
    if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
    return v;
  },
};

// ═════════════════════════════════════════════════════════════════════════════
// FORMULÁRIO 1 — REQUERIMENTO DE SERVIÇO COM DECLARAÇÃO DE AUTENTICIDADE
// ═════════════════════════════════════════════════════════════════════════════

const FormServicoDeclaracao = ({ cliente, placaInicial, chassiInicial, cidadeEmpresa, onVeiculoChange }) => {
  const [servicosSelecionados, setServicosSelecionados] = useState([]);
  const [subOpcoes, setSubOpcoes]     = useState({});
  const [outroServico, setOutroServico] = useState('');
  const [placa, setPlaca]             = useState(placaInicial);
  const [chassi, setChassi]           = useState(chassiInicial);
  const [restrFin, setRestrFin]       = useState('');
  const [tipoRestricao, setTipoRestricao] = useState('');
  const [tipoCredor, setTipoCredor]   = useState('');
  const [agenteFin, setAgenteFin]     = useState('');
  const [autoEmail, setAutoEmail]     = useState('');
  const [identidade, setIdentidade]   = useState(cliente?.identidade || '');
  const [semCelular, setSemCelular]   = useState(false);
  const [semEmail, setSemEmail]       = useState(false);

  useEffect(() => { setPlaca(placaInicial); setChassi(chassiInicial); }, [placaInicial, chassiInicial]);
  useEffect(() => { setIdentidade(cliente?.identidade || ''); }, [cliente]);
  useEffect(() => { if (onVeiculoChange) onVeiculoChange({ placa, chassi }); }, [placa, chassi, onVeiculoChange]);

  const hoje = new Date();
  const dataAtual = {
    dia: String(hoje.getDate()).padStart(2, '0'),
    mes: MESES[hoje.getMonth()],
    ano: hoje.getFullYear(),
  };

  const nomeCliente = (cliente?.nome || cliente?.razaoSocial || '').toUpperCase();
  const docCliente  = cliente?.cpf ? fmt.cpf(cliente.cpf) : cliente?.cnpj ? fmt.cnpj(cliente.cnpj) : '';

  const enderecoCompleto = () => {
    if (!cliente) return '';
    const p = [];
    if (cliente.logradouro) p.push(cliente.logradouro.toUpperCase());
    if (cliente.numero)     p.push(cliente.numero);
    if (cliente.complemento) p.push(cliente.complemento.toUpperCase());
    if (cliente.bairro)     p.push(`– ${cliente.bairro.toUpperCase()}`);
    if (cliente.cidade)     p.push(`– ${cliente.cidade.toUpperCase()}`);
    if (cliente.uf)         p.push(`/${cliente.uf.toUpperCase()}`);
    if (cliente.cep)        p.push(`– CEP ${cliente.cep}`);
    return p.join(' ');
  };

  const toggleServico = (id) =>
    setServicosSelecionados(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );

  const toggleSubOpcao = (servico, opcao) =>
    setSubOpcoes(prev => ({ ...prev, [servico]: prev[servico] === opcao ? '' : opcao }));

  const renderServico = (nome) => {
    const temSub = SERVICOS_COM_SUBOPCAO.includes(nome);
    return (
      <ServicoLinha key={nome}>
        <CheckWrap>
          <input type="checkbox" checked={servicosSelecionados.includes(nome)} onChange={() => toggleServico(nome)} />
          <span>{nome}</span>
        </CheckWrap>
        {temSub && (
          <SubOpcoes>
            {['furto/roubo', 'perda/extravio', 'outros'].map(op => (
              <SubOpcaoLabel key={op}>
                <input type="radio" name={`sub_${nome}`} checked={subOpcoes[nome] === op} onChange={() => toggleSubOpcao(nome, op)} />
                {op}
              </SubOpcaoLabel>
            ))}
          </SubOpcoes>
        )}
      </ServicoLinha>
    );
  };

  return (
    <FormContainer className="print-area">
      <Titulo>
        ANEXO 1 - REQUERIMENTO DE SERVIÇO COM DECLARAÇÃO DE AUTENTICIDADE DE DOCUMENTOS
      </Titulo>

      {/* ─── CORREÇÃO: linha "Eu, NOME, Identidade nº" toda em linha inline ─── */}
      <BlocoIdentificacao>
        <LinhaInline>
          <span>Eu,&nbsp;</span>
          <NomeDestaque>{nomeCliente}</NomeDestaque>
          <SpanLinha />{/* linha pontilhada que ocupa o espaço restante antes de "Identidade" */}
          <span style={{ whiteSpace: 'nowrap' }}>,&nbsp;Identidade nº&nbsp;</span>
          <InputLinha
            value={identidade}
            onChange={e => setIdentidade(e.target.value.toUpperCase())}
            style={{ width: '110px' }}
            placeholder="nº identidade"
          />
        </LinhaInline>

        <LinhaTexto>
          <span>CPF nº </span><ValorInline>{docCliente}</ValorInline>
          <span> , Telefone nº </span><ValorInline>{fmt.tel(cliente?.telefone)}</ValorInline>
          <span> requeiro ao DETRAN/RS a realização do seguinte serviço, relativo ao(s) veículo(s) placa(s) </span>
          <InputLinha value={placa} onChange={e => setPlaca(e.target.value.toUpperCase())} placeholder="ABC-1234" maxLength={8} style={{ width: '75px' }} />
          <span> , chassi(s) </span>
          <InputLinha value={chassi} onChange={e => setChassi(e.target.value.toUpperCase())} placeholder="12345678901234567890" maxLength={20} style={{ width: '175px' }} />
          <span> :</span>
        </LinhaTexto>
      </BlocoIdentificacao>

      <ServicosContainer>
        <ServicosColuna>
          {SERVICOS_ESQUERDA.map(renderServico)}
          <ServicoLinha>
            <CheckWrap>
              <input type="checkbox" checked={servicosSelecionados.includes('outro')} onChange={() => toggleServico('outro')} />
              <span>Outro:</span>
            </CheckWrap>
            <InputOutro type="text" value={outroServico} onChange={e => setOutroServico(e.target.value)} placeholder="Descreva o serviço" />
          </ServicoLinha>
        </ServicosColuna>
        <ServicosColuna>
          {SERVICOS_DIREITA.map(renderServico)}
        </ServicosColuna>
      </ServicosContainer>

      <LinhaRestricao>
        <Rotulo>Haverá inclusão ou alteração de restrição financeira?</Rotulo>
        <RadioLabel><input type="radio" name="restrFin" onChange={() => setRestrFin('Sim')} /> Sim</RadioLabel>
        <RadioLabel><input type="radio" name="restrFin" onChange={() => setRestrFin('Não')} /> Não</RadioLabel>
      </LinhaRestricao>

      <LinhaRestricao>
        {['Arrendamento', 'Reserva de Domínio', 'Alienação Fiduciária', 'Penhor', 'Comodato', 'Locação'].map(tipo => (
          <RadioLabel key={tipo}>
            <input type="radio" name="tipoRestricao" onChange={() => setTipoRestricao(tipo)} /> {tipo}
          </RadioLabel>
        ))}
      </LinhaRestricao>

      <LinhaRestricao>
        <Rotulo>O credor é:</Rotulo>
        <RadioLabel><input type="radio" name="tipoCredor" onChange={() => setTipoCredor('Pessoa Física')} /> Pessoa Física</RadioLabel>
        <RadioLabel><input type="radio" name="tipoCredor" onChange={() => setTipoCredor('Pessoa Jurídica')} /> Pessoa Jurídica</RadioLabel>
      </LinhaRestricao>

      <LinhaRestricao>
        <Rotulo>Agente Financeiro:</Rotulo>
        <InputLinhaCrescente value={agenteFin} onChange={e => setAgenteFin(e.target.value)} />
      </LinhaRestricao>

      <SecaoBox>
        <SecaoTitulo>
          DADOS DO ADQUIRENTE (PROCESSOS 101, 102 E 103) OU DO PROPRIETÁRIO (DEMAIS PROCESSOS)
        </SecaoTitulo>
        <SecaoSubtitulo>
          (Preenchimento obrigatório dos campos abaixo, sendo vedada a inclusão de quaisquer dados de terceiros)
        </SecaoSubtitulo>

        <LinhaCampo>
          <Rotulo>Endereço:</Rotulo>
          <CampoValor style={{ flex: 1 }}>{enderecoCompleto()}</CampoValor>
        </LinhaCampo>

        <LinhaCampo>
          <Rotulo>Telefone celular:</Rotulo>
          <CampoValor style={{ width: '120px', flex: 'none' }}>{fmt.tel(cliente?.telefone)}</CampoValor>
          <CheckboxLabel>
            <input type="checkbox" checked={semCelular} onChange={e => setSemCelular(e.target.checked)} />
            Não possui celular
          </CheckboxLabel>
          <Rotulo style={{ marginLeft: '10px' }}>Email:</Rotulo>
          <EmailValor>{semEmail ? '' : (cliente?.email || '').toLowerCase()}</EmailValor>
          <CheckboxLabel>
            <input type="checkbox" checked={semEmail} onChange={e => setSemEmail(e.target.checked)} />
            Não possui Email
          </CheckboxLabel>
        </LinhaCampo>

        <LinhaCampo>
          <Rotulo>Autorizo o DETRAN/RS a enviar por email ou telefone celular informações de interesse junto a este órgão.</Rotulo>
          <RadioLabel><input type="radio" name="autoEmail" onChange={() => setAutoEmail('SIM')} /> SIM</RadioLabel>
          <RadioLabel><input type="radio" name="autoEmail" onChange={() => setAutoEmail('NÃO')} /> NÃO</RadioLabel>
        </LinhaCampo>
      </SecaoBox>

      <DeclaracaoBox>
        <DeclaracaoTexto>
          <strong>
            DECLARO, sob as penas da lei e sem prejuízo de demais sanções administrativas/cíveis/criminais, que
            os documentos entregues ao DETRAN/RS para o serviço requerido são todos autênticos e, quando cópias,
            condizem com o original e, caso haja produção de placa(s) de identificação veicular, que sou responsável
            por providenciar a estampagem e instalação da(s) placa(s) no veículo para o qual a estampagem foi
            autorizada, bem como pela entrega das placas retiradas/substituídas na EPIV.
          </strong>
        </DeclaracaoTexto>
      </DeclaracaoBox>

      <AssinaturaContainer>
        <MunicipioLinha>
          Município: <strong>{(cidadeEmpresa || '').toUpperCase()}</strong>, {dataAtual.dia} de {dataAtual.mes} de {dataAtual.ano}.
        </MunicipioLinha>
        <EspacoAssinatura />
        <AssinaturaLinha />
        <AssinaturaRotulo>Assinatura do Responsável</AssinaturaRotulo>
      </AssinaturaContainer>
    </FormContainer>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// FORMULÁRIO 2 — REQUERIMENTO DE PRIMEIRO EMPLACAMENTO
// ═════════════════════════════════════════════════════════════════════════════

const FormPrimeiroEmplacamento = ({ cliente, chassiInicial, cidadeEmpresa, onVeiculoChange }) => {
  const [chassi, setChassi]         = useState(chassiInicial);
  const [identidade, setIdentidade] = useState(cliente?.identidade || '');
  const [restrFin, setRestrFin]     = useState('');
  const [tipoRestricao, setTipoRestricao] = useState('');
  const [tipoCredor, setTipoCredor] = useState('');
  const [agenteFin, setAgenteFin]   = useState('');
  const [autoEmail, setAutoEmail]   = useState('');
  const [semCelular, setSemCelular] = useState(false);
  const [semEmail, setSemEmail]     = useState(false);

  useEffect(() => { setChassi(chassiInicial); }, [chassiInicial]);
  useEffect(() => { setIdentidade(cliente?.identidade || ''); }, [cliente]);
  useEffect(() => { if (onVeiculoChange) onVeiculoChange({ chassi }); }, [chassi, onVeiculoChange]);

  const hoje = new Date();
  const dataAtual = {
    dia: String(hoje.getDate()).padStart(2, '0'),
    mes: MESES[hoje.getMonth()],
    ano: hoje.getFullYear(),
  };

  const nomeCliente = (cliente?.nome || cliente?.razaoSocial || '').toUpperCase();
  const docCpf = cliente?.cpf ? fmt.cpf(cliente.cpf) : cliente?.cnpj ? fmt.cnpj(cliente.cnpj) : '';

  const enderecoCompleto = () => {
    if (!cliente) return '';
    const p = [];
    if (cliente.logradouro) p.push(cliente.logradouro.toUpperCase());
    if (cliente.numero)     p.push(cliente.numero);
    if (cliente.complemento) p.push(cliente.complemento.toUpperCase());
    if (cliente.bairro)     p.push(`– ${cliente.bairro.toUpperCase()}`);
    if (cliente.cidade)     p.push(`– ${cliente.cidade.toUpperCase()}`);
    if (cliente.uf)         p.push(`/${cliente.uf.toUpperCase()}`);
    if (cliente.cep)        p.push(`– CEP ${cliente.cep}`);
    return p.join(' ');
  };

  return (
    <FormContainer className="print-area">
      <Titulo>
        ANEXO 1 - REQUERIMENTO DE SERVIÇO PARA O PROCESSO DE PRIMEIRO EMPLACAMENTO
      </Titulo>

      <BlocoIdentificacao>
        <LinhaNome>
          <span>Eu, </span>
          <NomeSpan>{nomeCliente}</NomeSpan>
          <span style={{ whiteSpace: 'nowrap' }}> , Identidade</span>
        </LinhaNome>
        <LinhaTexto>
          <span>nº </span>
          <InputLinha value={identidade} onChange={e => setIdentidade(e.target.value.toUpperCase())} style={{ width: '160px' }} placeholder="nº identidade" />
          <span> , CPF nº </span>
          <ValorInline>{docCpf}</ValorInline>
          <span> , Telefone nº </span>
          <ValorInline>{fmt.tel(cliente?.telefone)}</ValorInline>
          <span> , requeiro ao DETRAN/RS a realização do processo de primeiro emplacamento, relativo ao(s) veículo(s) chassi(s) </span>
          <InputLinha value={chassi} onChange={e => setChassi(e.target.value.toUpperCase())} placeholder="chassi(s)" maxLength={20} style={{ width: '180px' }} />
          <span> :</span>
        </LinhaTexto>
      </BlocoIdentificacao>

      <BlocoSeparado>
        <LinhaRestricao>
          <Rotulo>Haverá inclusão ou alteração de restrição financeira?</Rotulo>
          <RadioLabel><input type="radio" name="restrFinPE" onChange={() => setRestrFin('Sim')} /> Sim</RadioLabel>
          <RadioLabel><input type="radio" name="restrFinPE" onChange={() => setRestrFin('Não')} /> Não</RadioLabel>
        </LinhaRestricao>

        <LinhaRestricaoGrid>
          {['Arrendamento', 'Reserva de Domínio', 'Alienação Fiduciária', 'Penhor', 'Comodato', 'Locação'].map(tipo => (
            <RadioLabel key={tipo}>
              <input type="radio" name="tipoRestricaoPE" onChange={() => setTipoRestricao(tipo)} /> {tipo}
            </RadioLabel>
          ))}
        </LinhaRestricaoGrid>

        <LinhaRestricao>
          <Rotulo>O credor é:</Rotulo>
          <RadioLabel><input type="radio" name="tipoCredorPE" onChange={() => setTipoCredor('Pessoa Física')} /> Pessoa Física</RadioLabel>
          <RadioLabel><input type="radio" name="tipoCredorPE" onChange={() => setTipoCredor('Pessoa Jurídica')} /> Pessoa Jurídica</RadioLabel>
        </LinhaRestricao>

        <LinhaRestricao>
          <Rotulo>Agente Financeiro:</Rotulo>
          <InputLinhaCrescente value={agenteFin} onChange={e => setAgenteFin(e.target.value)} />
        </LinhaRestricao>
      </BlocoSeparado>

      <SecaoBox>
        <SecaoTitulo>DADOS DO ADQUIRENTE</SecaoTitulo>
        <SecaoSubtitulo>
          (Preenchimento obrigatório dos campos abaixo, sendo vedada a inclusão de quaisquer dados de terceiros)
        </SecaoSubtitulo>

        <LinhaCampo>
          <Rotulo>Endereço:</Rotulo>
          <CampoValor style={{ flex: 1 }}>{enderecoCompleto()}</CampoValor>
        </LinhaCampo>

        <LinhaCampo>
          <Rotulo>Telefone celular:</Rotulo>
          <CampoValor style={{ width: '120px', flex: 'none' }}>{fmt.tel(cliente?.telefone)}</CampoValor>
          <CheckboxLabel>
            <input type="checkbox" checked={semCelular} onChange={e => setSemCelular(e.target.checked)} />
            Não possui celular
          </CheckboxLabel>
          <Rotulo style={{ marginLeft: '10px' }}>Email:</Rotulo>
          <EmailValor>{semEmail ? '' : (cliente?.email || '').toLowerCase()}</EmailValor>
          <CheckboxLabel>
            <input type="checkbox" checked={semEmail} onChange={e => setSemEmail(e.target.checked)} />
            Não possui Email
          </CheckboxLabel>
        </LinhaCampo>

        <LinhaCampo>
          <Rotulo>Autorizo o DETRAN/RS a enviar por email ou telefone celular informações de interesse junto a este órgão.</Rotulo>
          <RadioLabel><input type="radio" name="autoEmailPE" onChange={() => setAutoEmail('SIM')} /> SIM</RadioLabel>
          <RadioLabel><input type="radio" name="autoEmailPE" onChange={() => setAutoEmail('NÃO')} /> NÃO</RadioLabel>
        </LinhaCampo>
      </SecaoBox>

      <DeclaracaoBox>
        <DeclaracaoTexto>
          <strong>DECLARO, sob as penas da lei e sem prejuízo de demais sanções administrativas/cíveis/criminais, que:</strong>
        </DeclaracaoTexto>
        <DeclaracaoTexto style={{ marginTop: '8px' }}>
          <strong>a)</strong> o(s) veículo(s) são novos (Zero quilômetro) e está(ão) fisicamente de acordo com o pré-cadastro na
          Base Indice Nacional – BIN da SENATRAN, Nota(s) Fiscal(is), bem como do Certificado de
          Adequação a Legislação de Trânsito – CAT e do Certificado de Capacitação Técnica – CCT, quando exigível(is);
        </DeclaracaoTexto>
        <DeclaracaoTexto style={{ marginTop: '6px' }}>
          <strong>b)</strong> estou ciente de que no caso de qualquer alteração do veículo em relação ao pré-cadastro e/ou com
          o CAT/CCT quando solicitados, é de minha responsabilidade informar ao Centro de Registro de
          Veículos Automotores – CRVA, para correção/alteração das informações do veículo antes do
          primeiro emplacamento, para fins de adequação à legislação de Trânsito.
        </DeclaracaoTexto>
        <DeclaracaoTexto style={{ marginTop: '6px' }}>
          <strong>c)</strong> os documentos entregues ao DETRAN/RS para o serviço requerido são todos autênticos e,
          quando cópias, condizem com o original, e que sou responsável por providenciar a estampagem e
          instalação da(s) placa(s) no veículo para o qual a estampagem foi autorizada.
        </DeclaracaoTexto>
      </DeclaracaoBox>

      <AssinaturaContainer>
        <MunicipioLinha>
          Município: <strong>{(cidadeEmpresa || '').toUpperCase()}</strong>, {dataAtual.dia} de {dataAtual.mes} de {dataAtual.ano}.
        </MunicipioLinha>
        <EspacoAssinatura />
        <AssinaturaLinha />
        <AssinaturaRotulo>Assinatura do Responsável</AssinaturaRotulo>
      </AssinaturaContainer>
    </FormContainer>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL — roteador de formulários
// ═════════════════════════════════════════════════════════════════════════════

// onVeiculoChange({ placa, chassi }): avisa a página dos dados do veículo como estão na folha
const RequerimentoDetranForm = ({ cliente, tipoRequerimento, placaInicial = '', chassiInicial = '', cidadeEmpresa = '', onVeiculoChange }) => {
  return (
    <>
      <PrintStyles />
      {tipoRequerimento === 'SERVICO_DECLARACAO' && (
        <FormServicoDeclaracao cliente={cliente} placaInicial={placaInicial} chassiInicial={chassiInicial} cidadeEmpresa={cidadeEmpresa} onVeiculoChange={onVeiculoChange} />
      )}
      {tipoRequerimento === 'PRIMEIRO_EMPLACAMENTO' && (
        <FormPrimeiroEmplacamento cliente={cliente} chassiInicial={chassiInicial} cidadeEmpresa={cidadeEmpresa} onVeiculoChange={onVeiculoChange} />
      )}
    </>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════

const FormContainer = styled.div`
  width: 210mm;
  margin: 0 auto;
  background: #fff;
  padding: 12mm 9mm;
  box-sizing: border-box;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 9.5pt;
  color: #000;
  border: 1px solid #ccc;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);

  @media print {
    border: none; box-shadow: none; margin: 0; padding: 0; width: 100%;
  }
`;

const Titulo = styled.div`
  font-size: 9.5pt;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 8px;
  letter-spacing: 0.2px;
`;

const BlocoIdentificacao = styled.div`
  margin-bottom: 5px;
  font-size: 9.5pt;
  line-height: 1.7;
`;

const BlocoSeparado = styled.div`
  margin: 12px 0 8px 0;
  font-size: 9.5pt;
`;

const LinhaRestricaoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px 8px;
  margin: 6px 0;
  font-size: 9.5pt;
`;

/* ── CORREÇÃO: linha "Eu, NOME , Identidade nº ___" sem quebra ── */
/* Usa display: flex com overflow controlado para não quebrar */
const LinhaInline = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: nowrap;
  font-size: 9.5pt;
  line-height: 1.7;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;

  span {
    font-size: 9.5pt;
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

/* Linha pontilhada que preenche o espaço entre o nome e "Identidade nº" */
const SpanLinha = styled.span`
  flex: 1 1 auto;
  border-bottom: 1px solid #000;
  min-width: 20px;
  display: inline-block;
  margin: 0 2px 2px 2px;
  align-self: flex-end;
`;

const NomeDestaque = styled.span`
  font-weight: 700;
  font-size: 9.5pt;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* LinhaNome mantida para os outros formulários */
const LinhaNome = styled.div`
  display: flex;
  align-items: baseline;
  white-space: nowrap;
  font-size: 9.5pt;
  margin-bottom: 2px;
`;

const NomeSpan = styled.span`
  font-weight: 700;
  font-size: 9.5pt;
  border-bottom: 1px solid #000;
  padding: 0 2px 1px;
  text-transform: uppercase;
  flex: 1;
`;

const LinhaTexto = styled.div`
  font-size: 9.5pt;
  line-height: 1.7;
  display: block;
  span { font-size: 9.5pt; }
`;

const ValorInline = styled.span`
  border-bottom: 1px solid #000;
  padding: 0 2px 1px;
  font-weight: 700;
  font-size: 9.5pt;
`;

const EmailValor = styled.span`
  border-bottom: 1px solid #000;
  padding: 0 2px 1px;
  font-size: 7.5pt;
  font-weight: 700;
  flex: 1;
  text-transform: none;
  letter-spacing: 0;
`;

const LinhaCampo = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 3px 5px;
  margin-bottom: 5px;
  font-size: 9.5pt;
  line-height: 1.8;
`;

const Rotulo = styled.span`
  white-space: nowrap;
  font-size: 9.5pt;
`;

const CampoValor = styled.span`
  border-bottom: 1px solid #000;
  padding: 0 2px 1px;
  font-weight: 700;
  font-size: 9.5pt;
  display: inline-block;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InputLinha = styled.input`
  border: none;
  border-bottom: 1px solid #000;
  outline: none;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 9.5pt;
  font-weight: 700;
  color: #000;
  background: transparent;
  padding: 0 2px 1px;
  text-transform: uppercase;
  flex-shrink: 0;

  @media print {
    color: #000;
    &::placeholder { color: transparent; }
  }
`;

const InputLinhaCrescente = styled.input`
  border: none;
  border-bottom: 1px solid #000;
  outline: none;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 9.5pt;
  background: transparent;
  padding: 0 2px 1px;
  flex: 1;
  min-width: 180px;

  @media print {
    &::placeholder { color: transparent; }
  }
`;

/* ---- Serviços ---- */

const ServicosContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 8px;
  border: 1px solid #000;
  margin: 7px 0;
  padding: 5px 7px;
`;

const ServicosColuna = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5px;
`;

const ServicoLinha = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 8.5pt;
  line-height: 1.45;
`;

const CheckWrap = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 8.5pt;

  input[type="checkbox"] {
    width: 10px; height: 10px; margin: 0;
    accent-color: #000; flex-shrink: 0;
  }
`;

const SubOpcoes = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 16px;
  font-size: 8.2pt;
`;

const SubOpcaoLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  font-size: 8.2pt;

  input[type="radio"] { width: 9px; height: 9px; margin: 0; }
`;

const InputOutro = styled.input`
  border: none;
  border-bottom: 1px solid #000;
  outline: none;
  font-size: 8.5pt;
  font-family: Arial, Helvetica, sans-serif;
  background: transparent;
  margin-left: 4px;
  width: 90%;
  margin-top: 2px;

  @media print {
    &::placeholder { color: transparent; }
  }
`;

/* ---- Restrição / Linhas ---- */

const LinhaRestricao = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  margin: 4px 0;
  font-size: 9.5pt;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 9.5pt;
  cursor: pointer;
  white-space: nowrap;

  input[type="radio"] { width: 10px; height: 10px; margin: 0; }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 9.5pt;
  cursor: pointer;
  white-space: nowrap;

  input[type="checkbox"] { width: 10px; height: 10px; margin: 0; }
`;

/* ---- Seções ---- */

const SecaoBox = styled.div`
  border: 1px solid #000;
  padding: 6px 8px;
  margin: 8px 0;
`;

const SecaoTitulo = styled.div`
  font-size: 9pt;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const SecaoSubtitulo = styled.div`
  font-size: 8.5pt;
  margin-bottom: 5px;
  font-style: italic;
`;

/* ---- Declaração ---- */

const DeclaracaoBox = styled.div`
  border: 1px solid #000;
  padding: 8px 10px;
  margin: 8px 0;
  page-break-inside: avoid;
`;

const DeclaracaoTexto = styled.p`
  font-size: 9pt;
  line-height: 1.55;
  margin: 0;
  text-align: justify;
`;

/* ---- Assinatura ---- */

const AssinaturaContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  page-break-inside: avoid;
`;

const MunicipioLinha = styled.div`
  font-size: 9.5pt;
  text-align: center;
  margin-bottom: 4px;
`;

const EspacoAssinatura = styled.div`
  height: 50px;
  width: 65%;
  border-bottom: 1px solid #000;
  box-sizing: border-box;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  @media print {
    border-bottom: 1px solid #000 !important;
    display: block !important;
    visibility: visible !important;
  }
`;

const AssinaturaLinha = styled.div`display: none;`;

const AssinaturaRotulo = styled.div`
  font-size: 9.5pt;
  text-align: center;
  max-width: 80%;
`;


export default RequerimentoDetranForm;
